import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { filterProjects, allProjects } from '../data/allProjects';

export type StatusFilter = 'all' | 'current' | 'completed';

export interface WorkFilters {
  industry: string;
  tech: string;
  status: StatusFilter;
}

export function useWorkFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read from URL params or use defaults
  const filters: WorkFilters = useMemo(
    () => ({
      industry: searchParams.get('industry') || 'all',
      tech: searchParams.get('tech') || 'all',
      status: (searchParams.get('status') as StatusFilter) || 'all',
    }),
    [searchParams]
  );

  // Update URL params
  const setFilter = useCallback(
    (key: keyof WorkFilters, value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const setIndustry = useCallback(
    (industry: string) => setFilter('industry', industry),
    [setFilter]
  );

  const setTech = useCallback(
    (tech: string) => setFilter('tech', tech),
    [setFilter]
  );

  const setStatus = useCallback(
    (status: StatusFilter) => setFilter('status', status),
    [setFilter]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Filtered projects
  const filteredProjects = useMemo(
    () => filterProjects(filters),
    [filters]
  );

  // Stats
  const stats = useMemo(
    () => ({
      total: allProjects.length,
      filtered: filteredProjects.length,
      current: allProjects.filter((p) => p.current).length,
      completed: allProjects.filter((p) => !p.current).length,
    }),
    [filteredProjects]
  );

  const hasActiveFilters = useMemo(
    () => filters.industry !== 'all' || filters.tech !== 'all' || filters.status !== 'all',
    [filters]
  );

  return {
    filters,
    setIndustry,
    setTech,
    setStatus,
    clearFilters,
    filteredProjects,
    stats,
    hasActiveFilters,
  };
}
